// Shared library announcement banner, currently powered by maintenance state.

import React, { useEffect, useState } from 'react';
import { MegaphoneIcon } from 'lucide-react';
import { getMaintenanceBanner } from '../../transport/api/publicApi';
import { MaintenanceBannerState } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('MaintenanceBanner');

const DEFAULT_STATE: MaintenanceBannerState = {
  status: 'off',
  message: null,
  updatedAt: 0,
  updatedBy: null,
};

const MaintenanceBanner: React.FC = () => {
  const [state, setState] = useState<MaintenanceBannerState>(DEFAULT_STATE);

  useEffect(() => {
    async function loadBanner() {
      try {
        const response = await getMaintenanceBanner();
        setState(response.data ?? DEFAULT_STATE);
      } catch (error) {
        logger.warn('Failed to load maintenance banner', error);
        setState(DEFAULT_STATE);
      }
    }

    loadBanner();
  }, []);

  const message = state.message?.trim() || null;
  const isVisible = state.status === 'on';

  if (!isVisible || !message) {
    return null;
  }

  return (
    <div className="border-b border-amber-300/80 bg-amber-100/85 text-amber-950 dark:border-amber-400/25 dark:bg-amber-500/12 dark:text-amber-100">
      <div
        role="status"
        aria-live="polite"
        className="mx-auto flex min-h-[3.25rem] w-full max-w-screen-2xl items-center px-4 sm:px-6 lg:px-8"
      >
        <div className="flex w-full items-center justify-center gap-2.5 text-center">
          <MegaphoneIcon
            aria-hidden="true"
            className="shrink-0 text-amber-700 dark:text-amber-300"
          />
          <p className="text-sm font-medium text-current">{message}</p>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceBanner;
